import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, CircularProgress, Grid, LinearProgress, Typography } from '@mui/material';
import {
    Lock as LockIcon,
    LockOpen as LockOpenIcon,
    CheckCircle as CheckCircleIcon,
    Create as DrawIcon,
    Article as ArticleIcon,
    Download as DownloadIcon,
    Gavel as GavelIcon,
} from '@mui/icons-material';
import { Document, Page, pdfjs } from 'react-pdf';
import { PDFDocument } from 'pdf-lib';
import SignaturePad from 'react-signature-canvas';
import moment from 'moment';
import Teachr from 'src/Objects/models/Teachr';
import { Address } from 'src/Objects/models/Address';
import AddressRepository from 'src/Repositories/AddressRepository';
import ImageRepository from 'src/Repositories/ImageRepository';
import TeachrRepository from 'src/Repositories/TeachrRepository';
import { userTeachrSetAccountAction } from 'src/Redux/Actions/UserActions';
import Sponsorship from 'src/Objects/models/Sponsorship';
import { PutSponsorship } from 'src/Objects/interfaces/AppInterface';
import { putSponsorship } from 'src/Redux/Actions/SponsorshipActions';
import { AppDispatch } from 'src/Redux/store';
import useTailwindBrand from 'src/Theme/useTailwindBrand';
import ButtonField from 'src/Components/Ui/ButtonField';
import Settings from 'src/Components/Settings';
import { ENV_DECLARONS, isBrandActive } from 'src/config/environment.config';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`;
const PDF_PATH = `${process.env.PUBLIC_URL}/${isBrandActive(ENV_DECLARONS) ? "Conditions_de_services_VDEF_declarons.pdf" : 'Conditions_de_services_VDEF.pdf'}`;
// 

type CondStep = 'accept' | 'sign' | 'done';

interface ThirdCompletProfilProps {
    nextStep: (step: number) => void;
    teachr: Teachr;
}

const buildFilledPdf = async (
    teachr: Teachr,
    address: Address | undefined,
    options: { signatureDataURL?: string | null } = {}
): Promise<Blob> => {
    const { signatureDataURL = null } = options;
    const resPdf = await fetch(PDF_PATH);
    const arrayBuffer = await resPdf.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    const form = pdfDoc.getForm();

    const fill = (name: string, value: string) => {
        try { form.getTextField(name).setText(value); } catch { /* field may not exist */ }
    };

    fill('name', `${teachr.firstName ?? ''} ${teachr.lastName ?? ''}`.trim());
    fill('siret', teachr?.RCS ?? '');
    fill('address-postale', address?.formattedAddress ?? '');
    fill('city', address?.city ?? '');
    fill('date', moment().format('L'));

    if (signatureDataURL) {
        try {
            const pages = pdfDoc.getPages();
            const imageBytes = await fetch(signatureDataURL).then(r => r.arrayBuffer());
            const image = await pdfDoc.embedPng(imageBytes);
            const imageWidth = 100;
            const imageHeight = 30;
            // Les deux documents ont une mise en page et un nombre de pages
            // différents : la signature ne se place pas aux mêmes endroits.
            //  - fin de convention (sous « L'enseignant » / « L'intervenant »)
            //  - annexe mandat (sous « Signature de l'auto-entrepreneur »)
            const signatureSpots = isBrandActive(ENV_DECLARONS)
                ? [
                    { page: 6, x: 315, y: 700 },
                    { page: 7, x: 80, y: 440 },
                ]
                : [
                    { page: 8, x: pages[8].getWidth() / 2 + imageWidth / 2, y: imageWidth * 2 + 20 },
                    { page: 9, x: 80, y: imageWidth * 3 },
                ];
            for (const spot of signatureSpots) {
                pages[spot.page]?.drawImage(image, {
                    x: spot.x,
                    y: spot.y,
                    width: imageWidth,
                    height: imageHeight,
                });
            }
        } catch { /* signature position not found */ }
    }

    try { form.flatten(); } catch { /* some PDFs resist flattening */ }
    const pdfBytes = await pdfDoc.save();
    return new Blob([pdfBytes.buffer as ArrayBuffer], { type: 'application/pdf' });
};

const ThirdCompletProfil: React.FC<ThirdCompletProfilProps> = ({ nextStep, teachr }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { brandConfig } = useTailwindBrand();
    const primaryHex = brandConfig.colors?.primary ?? '#1976d2';
    const fontFamily = brandConfig.fontFamily;

    const [address, setAddress] = useState<Address | undefined>();

    const [numPages, setNumPages] = useState(0);
    const [pdfWidth, setPdfWidth] = useState(600);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [scrollProgress, setScrollProgress] = useState(0);
    const hasScrolledToEnd = scrollProgress >= 99.5;

    const [condStep, setCondStep] = useState<CondStep>('accept');

    const [signatureDataURL, setSignatureDataURL] = useState<string | null>(null);
    const [showSignPad, setShowSignPad] = useState(true);
    const padRef = useRef<SignaturePad>(null);

    const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);

    const [isGenerating, setIsGenerating] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

    useEffect(() => {
        if (teachr?.address?.id) {
            AddressRepository.fetchAddressById(teachr.address.id)
                .then(setAddress)
                .catch(() => {});
        }
    }, [teachr]);

    useEffect(() => {
        const update = () => {
            const c = scrollRef.current;
            if (c) setPdfWidth(Math.min(c.clientWidth - 16, 800));
        };
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const onScroll = () => {
            const max = el.scrollHeight - el.clientHeight;
            if (max <= 0) return;
            setScrollProgress(prev => Math.max(prev, Math.min((el.scrollTop / max) * 100, 100)));
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        let objectUrl: string | null = null;
        setNumPages(0);
        setIsLoadingPreview(true);
        buildFilledPdf(teachr, address)
            .then(blob => {
                objectUrl = URL.createObjectURL(blob);
                setPreviewBlobUrl(objectUrl);
            })
            .catch(() => {})
            .finally(() => setIsLoadingPreview(false));
        return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [teachr, address]);

    const handleSignatureClear = () => {
        padRef.current?.clear();
        setSignatureDataURL(null);
        setShowSignPad(true);
    };

    const handleSignatureTrim = useCallback(() => {
        const url = padRef.current?.getTrimmedCanvas().toDataURL('image/png');
        if (url) setSignatureDataURL(url);
    }, []);

    const handleSignatureValidate = () => {
        if (!signatureDataURL) return;
        setShowSignPad(false);
    };

    const handleSubmit = async () => {
        if (!signatureDataURL) return;
        setIsGenerating(true);
        try {
            const blob = await buildFilledPdf(teachr, address, { signatureDataURL });
            const url = URL.createObjectURL(blob);
            setDownloadUrl(url);

            const imageResponse = await ImageRepository.createImage(
                new File([blob], 'proof-is-sign-mandat', { type: 'application/pdf' })
            );

            const updatedTeachr = {
                ...teachr,
                proofMandat: imageResponse['@id'],
                // proofIdentity: teachr.proofIdentity?.['@id'],
                // proofIdentity2: teachr.proofIdentity2?.['@id'],
                // proofSignSap: teachr.proofSignSap?.['@id'],
                // proofIsAE: teachr.proofIsAE?.['@id'],
            };

            const teachrResponse = await TeachrRepository.updateTeachr(updatedTeachr);
            const serializedTeachr = JSON.parse(JSON.stringify(teachrResponse));

            if (teachrResponse?.sponsorship) {
                const sponsorship = new Sponsorship(teachrResponse.sponsorship);
                sponsorship.statut = 'OK_CONDITION';
                const data: PutSponsorship = { teachr, sponsorship, setLoad: () => {}, handleNext: () => {} };
                dispatch(putSponsorship(data));
            }

            dispatch(userTeachrSetAccountAction(serializedTeachr));
            dispatch({ type: 'SET_TEACHR_INFO', payload: { teachr: serializedTeachr } });
            setCondStep('done');
        } catch (error) {
            console.error('Erreur lors de la soumission:', error);
        } finally {
            setIsGenerating(false);
        }
    };

    const sectionTitle = (icon: React.ReactNode, title: string, subtitle?: string) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: `${primaryHex}1a`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {React.cloneElement(icon as React.ReactElement, { sx: { color: primaryHex, fontSize: 20 } })}
            </Box>
            <Box>
                <Typography variant="subtitle1" fontWeight="700" fontFamily={fontFamily} color="text.primary">{title}</Typography>
                {subtitle && <Typography variant="caption" color="text.secondary" fontFamily={fontFamily}>{subtitle}</Typography>}
            </Box>
        </Box>
    );

    const stepIndicators = [
        { key: 'accept', label: '1. Acceptation' },
        { key: 'sign', label: '2. Signature' },
    ];
    const stepOrder: CondStep[] = ['accept', 'sign', 'done'];
    const isLocked = !hasScrolledToEnd;

    return (
        <Settings
            title="Conditions de services"
            subtitle="Lisez attentivement le document puis signez pour finaliser."
            icon={<GavelIcon sx={{ color: primaryHex, fontSize: 28 }} />}
            height="100%"
        >
            {/* ── PDF reader ── */}
            <Grid item xs={12}>
                <Box sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'grey.200', bgcolor: 'grey.50' }}>
                    {sectionTitle(<ArticleIcon />, 'Conditions de services', "Faites défiler jusqu'à la dernière page pour débloquer la signature")}

                    <Box sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" fontFamily={fontFamily}>Progression de lecture</Typography>
                            <Typography variant="caption" fontWeight="700" sx={{ color: !isLocked ? '#16a34a' : primaryHex, fontFamily }}>
                                {!isLocked ? '✓ Lu entièrement' : `${Math.round(scrollProgress)}%`}
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={scrollProgress}
                            sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: 'grey.200',
                                '& .MuiLinearProgress-bar': {
                                    bgcolor: !isLocked ? '#16a34a' : primaryHex,
                                    borderRadius: 3,
                                },
                            }}
                        />
                    </Box>

                    <Box
                        ref={scrollRef}
                        sx={{
                            height: 520,
                            overflowY: 'auto',
                            bgcolor: '#525659',
                            borderRadius: 1.5,
                            p: 1,
                            scrollbarWidth: 'thin',
                            scrollbarColor: `${primaryHex} #ccc`,
                        }}
                    >
                        {isLoadingPreview && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 1.5 }}>
                                <CircularProgress sx={{ color: 'white' }} size={32} />
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Chargement du document pré-rempli…</Typography>
                            </Box>
                        )}
                        {!isLoadingPreview && (
                            <Document
                                file={previewBlobUrl ?? PDF_PATH}
                                onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                                loading={
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                                        <CircularProgress sx={{ color: 'white' }} />
                                    </Box>
                                }
                            >
                                {Array.from({ length: numPages }, (_, i) => (
                                    <Box key={i} sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}>
                                        <Page pageNumber={i + 1} width={pdfWidth} renderTextLayer={false} renderAnnotationLayer={false} />
                                    </Box>
                                ))}
                            </Document>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: !isLocked ? '#f0fdf4' : '#fffbeb', border: '1px solid', borderColor: !isLocked ? '#bbf7d0' : '#fde68a' }}>
                        {!isLocked
                            ? <LockOpenIcon sx={{ color: '#16a34a', fontSize: 18 }} />
                            : <LockIcon sx={{ color: '#d97706', fontSize: 18 }} />
                        }
                        <Typography variant="caption" fontWeight="600" sx={{ color: !isLocked ? '#166534' : '#92400e', fontFamily }}>
                            {!isLocked
                                ? 'Document lu entièrement, vous pouvez maintenant accepter les conditions.'
                                : "Faites défiler jusqu'à la fin du document pour débloquer."}
                        </Typography>
                    </Box>
                </Box>
            </Grid>

            {/* ── Acceptance & Signature section ── */}
            <Grid item xs={12}>
                <Box
                    sx={{
                        p: 2.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        bgcolor: !isLocked ? 'background.paper' : 'grey.100',
                        opacity: !isLocked ? 1 : 0.45,
                        pointerEvents: !isLocked ? 'auto' : 'none',
                        transition: 'opacity 0.4s',
                        position: 'relative',
                    }}
                >
                    {isLocked && (
                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <LockIcon sx={{ color: '#d97706', fontSize: 36 }} />
                                <Typography variant="body2" fontWeight="700" color="#92400e" fontFamily={fontFamily} textAlign="center">
                                    Lisez le document en entier pour débloquer
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {sectionTitle(<DrawIcon />, 'Acceptation & Signature', 'Complétez les deux étapes pour finaliser')}

                    {/* Step indicators */}
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
                        {stepIndicators.map(({ key, label }) => {
                            const current = stepOrder.indexOf(condStep);
                            const thisIdx = stepOrder.indexOf(key as CondStep);
                            const isDone = current > thisIdx || condStep === 'done';
                            const isActive = key === condStep;
                            return (
                                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, borderRadius: 10, border: '1px solid', borderColor: isDone ? '#bbf7d0' : isActive ? primaryHex : 'grey.300', bgcolor: isDone ? '#f0fdf4' : isActive ? `${primaryHex}12` : 'transparent' }}>
                                    {isDone
                                        ? <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 14 }} />
                                        : <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: isActive ? primaryHex : 'grey.300' }} />
                                    }
                                    <Typography variant="caption" fontWeight="600" sx={{ color: isDone ? '#166534' : isActive ? primaryHex : 'text.secondary', fontFamily }}>
                                        {label}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* ── Step 1: Accept ── */}
                        {condStep === 'accept' && (
                            <Box sx={{ p: 2.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300', borderRadius: 2 }}>
                                <Typography variant="subtitle2" fontWeight="700" color="text.primary" sx={{ fontFamily, mb: 1 }}>
                                    Acceptation des conditions
                                </Typography>
                                <Box sx={{ mb: 2, p: 2, bgcolor: 'white', borderRadius: 1.5, border: '1px solid', borderColor: 'grey.200' }}>
                                    <Typography variant="body2" color="text.secondary" fontFamily={fontFamily} sx={{ mb: 1.5 }}>
                                        En acceptant, vous confirmez avoir pris connaissance et accepter :
                                    </Typography>
                                    {[
                                        'Les conditions de services',
                                        "Les conditions générales d'utilisation",
                                        'La politique de confidentialité',
                                    ].map((c, i) => (
                                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                                            <CheckCircleIcon sx={{ color: primaryHex, fontSize: 16, mt: 0.3, flexShrink: 0 }} />
                                            <Typography variant="body2" fontFamily={fontFamily} color="text.primary">{c}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                                <Box
                                    onClick={() => setCondStep('sign')}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 1.5, border: '1px dashed', borderColor: primaryHex, cursor: 'pointer', userSelect: 'none', '&:hover': { bgcolor: `${primaryHex}08` }, transition: 'background 0.2s' }}
                                >
                                    <Box sx={{ width: 20, height: 20, borderRadius: 0.5, border: '2px solid', borderColor: primaryHex, flexShrink: 0 }} />
                                    <Typography variant="body2" fontWeight="600" color="text.primary" fontFamily={fontFamily}>
                                        J'ai lu et j'accepte les conditions de services
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* ── Acceptance confirmed banner ── */}
                        {condStep !== 'accept' && (
                            <Box sx={{ p: 1.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                                <Typography variant="caption" fontWeight="700" color="#166534" fontFamily={fontFamily}>Conditions acceptées</Typography>
                            </Box>
                        )}

                        {/* ── Step 2: Sign ── */}
                        {condStep === 'sign' && (
                            <Box sx={{ p: 2.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300', borderRadius: 2 }}>
                                <Typography variant="subtitle2" fontWeight="700" color="text.primary" sx={{ fontFamily, mb: 0.5 }}>
                                    Votre signature
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontFamily, mb: 1.5 }}>
                                    Signez dans le cadre ci-dessous. Vérifiez que votre signature est lisible avant de valider.
                                </Typography>

                                {showSignPad ? (
                                    <Box sx={{ bgcolor: 'white', borderRadius: 1.5, border: '1px solid', borderColor: 'grey.300', p: 1, position: 'relative' }}>
                                        {!signatureDataURL && (
                                            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none', textAlign: 'center' }}>
                                                <Typography variant="body2" color="grey.400" fontWeight="600" fontFamily={fontFamily}>
                                                    Signer ici
                                                </Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                                            <button
                                                onClick={handleSignatureClear}
                                                className="text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors"
                                            >
                                                Effacer
                                            </button>
                                        </Box>
                                        <SignaturePad
                                            ref={padRef}
                                            onEnd={handleSignatureTrim}
                                            onBegin={() => setSignatureDataURL(null)}
                                            velocityFilterWeight={0.2}
                                            maxWidth={1.5}
                                            minWidth={1}
                                            canvasProps={{ className: 'w-full', style: { height: 160, cursor: 'crosshair' } }}
                                        />
                                    </Box>
                                ) : (
                                    signatureDataURL && (
                                        <Box sx={{ bgcolor: 'white', borderRadius: 1.5, border: '1px solid #bbf7d0', p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <img src={signatureDataURL} alt="Signature" style={{ maxHeight: 64, maxWidth: '60%', objectFit: 'contain' }} />
                                            <ButtonField label="Modifier" variant="outlined" className="font-bold" onClick={handleSignatureClear} />
                                        </Box>
                                    )
                                )}

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5, gap: 1.5 }}>
                                    {showSignPad ? (
                                        <ButtonField
                                            label="Valider la signature"
                                            variant="contained"
                                            className="font-bold"
                                            onClick={handleSignatureValidate}
                                            disabled={!signatureDataURL}
                                        />
                                    ) : (
                                        <ButtonField
                                            label={isGenerating ? 'Génération en cours...' : 'Générer le document signé'}
                                            variant="contained"
                                            className="font-bold"
                                            onClick={handleSubmit}
                                            disabled={isGenerating}
                                            startIcon={isGenerating ? <CircularProgress size={16} sx={{ color: 'white' }} /> : undefined}
                                        />
                                    )}
                                </Box>
                            </Box>
                        )}

                        {/* ── Done ── */}
                        {condStep === 'done' && downloadUrl && (
                            <Box sx={{ p: 2.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                    <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 24 }} />
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight="700" color="#166534" fontFamily={fontFamily}>
                                            Document signé !
                                        </Typography>
                                        <Typography variant="body2" color="#166534" fontFamily={fontFamily}>
                                            Vous pouvez télécharger votre exemplaire ou continuer.
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <a href={downloadUrl} download="conditions-services-signe.pdf" style={{ textDecoration: 'none' }}>
                                        <ButtonField
                                            label="Télécharger mon exemplaire"
                                            variant="outlined"
                                            className="font-bold"
                                            startIcon={<DownloadIcon />}
                                        />
                                    </a>
                                    <ButtonField
                                        label="Continuer →"
                                        variant="contained"
                                        className="font-bold"
                                        onClick={() => nextStep(6)}
                                    />
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Grid>
        </Settings>
    );
};

export default ThirdCompletProfil;
